import React, { useEffect, useState, useCallback } from "react";
import { Plus, Search, Trash2, RefreshCw, AlertCircle, Calendar, User, Phone, Package } from "lucide-react";
import toast from "react-hot-toast";
import { DataTable } from "../../DataTable/DataTable";
import { ProductEnquiryItem, ProductEnquiryService } from "../../../services/api/productEnquiry.service";
import CreateProductEnquiryModal from "./CreateProductEnquiryModal";

interface ProductEnquiryTabProps {
  clinicId: string;
}

const ProductEnquiryTab: React.FC<ProductEnquiryTabProps> = ({ clinicId }) => {
  const [enquiries, setEnquiries] = useState<ProductEnquiryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deletingModalItem, setDeletingModalItem] = useState<ProductEnquiryItem | null>(null);

  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 1,
    totalItemsCount: 0,
  });

  const fetchEnquiries = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ProductEnquiryService.getEnquiries({
        clinic_id: clinicId,
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: searchTerm.trim() || undefined,
      });

      if (res.success && Array.isArray(res.data)) {
        setEnquiries(res.data);
        if (res.pagination) {
          setPagination((prev) => ({
            ...prev,
            totalPages: res.pagination.totalPages || 1,
            totalItemsCount: res.pagination.totalItemsCount || res.data.length,
          }));
        }
      } else {
        setError(res.message || "Failed to load product enquiries");
        setEnquiries([]);
      }
    } catch (err) {
      console.error("Error fetching enquiries:", err);
      setError("An unexpected error occurred while fetching enquiries.");
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  }, [clinicId, pagination.page, pagination.pageSize, searchTerm]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      const res = await ProductEnquiryService.deleteEnquiry(id);
      if (res.success) {
        toast.success(res.message || "Enquiry deleted successfully");
        setDeletingModalItem(null);
        fetchEnquiries();
      } else {
        toast.error(res.message || "Failed to delete enquiry");
      }
    } catch (err) {
      toast.error("Failed to delete enquiry");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      key: "enquiry_item",
      title: "Product / Item Enquired",
      render: (item: ProductEnquiryItem) => (
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg flex-shrink-0">
            <Package className="w-4 h-4" />
          </div>
          <span className="font-semibold text-gray-900 text-sm">{item.enquiry_item}</span>
        </div>
      ),
    },
    {
      key: "patient_name",
      title: "Patient Details",
      render: (item: ProductEnquiryItem) => (
        <div className="space-y-0.5">
          <div className="flex items-center text-xs text-gray-800 font-medium">
            <User className="w-3.5 h-3.5 text-gray-400 mr-1.5" />
            {item.patient_name || <span className="text-gray-400 font-normal italic">Anonymous</span>}
          </div>
          {item.patient_mobile_number && (
            <div className="flex items-center text-xs text-gray-500">
              <Phone className="w-3.5 h-3.5 text-gray-400 mr-1.5" />
              {item.patient_mobile_number}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "created_at",
      title: "Enquiry Date",
      render: (item: ProductEnquiryItem) => (
        <div className="flex items-center text-xs text-gray-600">
          <Calendar className="w-3.5 h-3.5 text-gray-400 mr-1.5" />
          {formatDate(item.created_at)}
        </div>
      ),
    },
    {
      key: "action",
      title: "Action",
      width: "w-[100px]",
      render: (item: ProductEnquiryItem) => (
        <button
          onClick={() => setDeletingModalItem(item)}
          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete Enquiry"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-100 shadow-xs">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by item name or patient details..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="w-full pl-9 pr-4 py-2 bg-gray-50/50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
          />
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => fetchEnquiries()}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-emerald-600" : ""}`} />
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-xs"
          >
            <Plus className="w-4 h-4" />
            <span>New Enquiry</span>
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-xs overflow-hidden">
        {error ? (
          <div className="p-8 text-center space-y-3">
            <div className="inline-flex p-3 bg-red-50 text-red-500 rounded-full">
              <AlertCircle className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-gray-800">{error}</p>
            <button
              onClick={fetchEnquiries}
              className="px-4 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : enquiries.length > 0 ? (
          <DataTable
            data={enquiries}
            columns={columns}
            isLoading={loading}
            pagination={{
              currentPage: pagination.page,
              totalPages: pagination.totalPages,
              totalItemsCount: pagination.totalItemsCount,
              pageSize: pagination.pageSize,
              setCurrentPage: (page) => setPagination((prev) => ({ ...prev, page })),
              setPageSize: (pageSize) => setPagination((prev) => ({ ...prev, pageSize, page: 1 })),
            }}
          />
        ) : (
          <div className="p-12 text-center space-y-3">
            <div className="inline-flex p-4 bg-gray-50 text-gray-400 rounded-full">
              <Package className="w-8 h-8" />
            </div>
            <p className="text-sm font-medium text-gray-600">No product enquiries found</p>
            <p className="text-xs text-gray-400 max-w-sm mx-auto">
              Click the "New Enquiry" button to create an enquiry for products or stocks.
            </p>
          </div>
        )}
      </div>

      {/* Create Enquiry Modal */}
      <CreateProductEnquiryModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={fetchEnquiries}
        clinicId={clinicId}
      />

      {/* Delete Confirmation Modal */}
      {deletingModalItem && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4 shadow-xl border border-gray-100">
            <div className="flex items-center space-x-3 text-red-600">
              <div className="p-2 bg-red-50 rounded-lg">
                <Trash2 className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Product Enquiry</h3>
            </div>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete the enquiry for{" "}
              <strong className="text-gray-900">{deletingModalItem.enquiry_item}</strong>? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                onClick={() => setDeletingModalItem(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deletingModalItem.id)}
                disabled={deletingId === deletingModalItem.id}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deletingId === deletingModalItem.id ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductEnquiryTab;
